import { Lead, MAX_LEAD_IMAGES } from "../models/Lead.js";
import { formatLeadIntakeDescription, } from "../models/leadIntake.js";
import { ViewModel } from "../mvvm/ViewModel.js";
/**
 * LeadViewModel powers the structured marketing lead form.
 */
export class LeadViewModel extends ViewModel {
    onSubmit;
    presignUpload;
    options;
    constructor(onSubmit, presignUpload, prefill = {}, options = {}) {
        const lines = [];
        if (prefill.serviceTier)
            lines.push(`Service: ${prefill.serviceTier}`);
        if (prefill.packageTier)
            lines.push(`Package: ${prefill.packageTier}`);
        if (prefill.addonKeys?.length)
            lines.push(`Add-ons: ${prefill.addonKeys.join(", ")}`);
        if (prefill.photographyAddon)
            lines.push("Add-on: Custom Brand Photography");
        super({
            businessName: "",
            contactName: "",
            email: "",
            phone: "",
            domainStatus: "",
            projectDescription: lines.length ? `${lines.join("\n")}\n\n` : "",
            notes: "",
            existingWebsite: "",
            businessType: "",
            imageKeys: [],
            pendingFiles: [],
            errors: [],
            submitting: false,
            submitted: false,
            serviceTier: prefill.serviceTier ?? "",
            intakeServiceTier: prefill.serviceTier ?? "",
            packageTier: prefill.packageTier ?? "",
            photographyAddon: prefill.photographyAddon ?? false,
            addonKeys: prefill.addonKeys ? [...prefill.addonKeys] : [],
        });
        this.onSubmit = onSubmit;
        this.presignUpload = presignUpload;
        this.options = options;
    }
    setBusinessName(businessName) {
        this.setState({ businessName });
    }
    setContactName(contactName) {
        this.setState({ contactName });
    }
    setBusinessType(businessType) {
        this.setState({ businessType });
    }
    setNotes(notes) {
        this.setState({ notes });
    }
    setExistingWebsite(existingWebsite) {
        this.setState({ existingWebsite });
    }
    setIntakeServiceTier(intakeServiceTier) {
        this.setState({
            intakeServiceTier,
            serviceTier: intakeServiceTier && intakeServiceTier !== "not_sure" ? intakeServiceTier : "",
        });
    }
    setEmail(email) {
        this.setState({ email });
    }
    setPhone(phone) {
        this.setState({ phone });
    }
    setDomainStatus(domainStatus) {
        this.setState({ domainStatus });
    }
    setProjectDescription(projectDescription) {
        this.setState({ projectDescription });
    }
    async addImages(files) {
        const list = Array.from(files);
        if (this.state.imageKeys.length + list.length > MAX_LEAD_IMAGES) {
            this.setState({ errors: [`You can attach up to ${MAX_LEAD_IMAGES} images.`] });
            return false;
        }
        const keys = [...this.state.imageKeys];
        const names = [...this.state.pendingFiles];
        for (const file of list) {
            const up = await this.presignUpload(file);
            const headers = {
                "Content-Type": up.contentType ?? (file.type || "application/octet-stream"),
            };
            if (up.cacheControl)
                headers["Cache-Control"] = up.cacheControl;
            await fetch(up.uploadUrl, { method: up.method, body: file, headers });
            keys.push(up.key);
            names.push(file.name);
        }
        this.setState({ imageKeys: keys, pendingFiles: names, errors: [] });
        return true;
    }
    intakeValidationErrors() {
        if (!this.options.intakeForm)
            return [];
        const errors = [];
        if (this.state.contactName.trim() === "")
            errors.push("Contact name is required.");
        if (!this.state.businessType)
            errors.push("Select a business type.");
        if (!this.state.intakeServiceTier)
            errors.push("Pick the product you are interested in.");
        return errors;
    }
    projectDescriptionForSubmit() {
        if (!this.options.intakeForm)
            return this.state.projectDescription;
        const prefillBlock = this.state.projectDescription.trim();
        return formatLeadIntakeDescription({
            contactName: this.state.contactName,
            businessType: this.state.businessType,
            notes: this.state.notes,
            existingWebsite: this.state.existingWebsite,
            addons: this.state.addonKeys,
            ...(prefillBlock ? { prefillBlock } : {}),
        });
    }
    async submit() {
        const intakeErrors = this.intakeValidationErrors();
        if (intakeErrors.length > 0) {
            this.setState({ errors: intakeErrors });
            return false;
        }
        const description = this.projectDescriptionForSubmit();
        const lead = new Lead(this.state.businessName, this.state.email, this.state.phone, this.state.domainStatus, description, this.state.imageKeys);
        const errors = lead.validate();
        if (errors.length > 0) {
            this.setState({ errors });
            return false;
        }
        this.setState({ submitting: true, errors: [] });
        try {
            const prefill = {};
            const tier = this.state.intakeServiceTier === "not_sure"
                ? "not_sure"
                : this.state.serviceTier || undefined;
            if (tier)
                prefill.serviceTier = tier;
            if (this.state.packageTier)
                prefill.packageTier = this.state.packageTier;
            if (this.state.photographyAddon)
                prefill.photographyAddon = true;
            await this.onSubmit(lead, prefill);
            this.setState({ submitting: false, submitted: true });
            return true;
        }
        catch (err) {
            const status = err && typeof err === "object" && "status" in err
                ? Number(err.status)
                : 0;
            const message = status === 429
                ? "You have sent several requests recently. Please wait a minute and try again."
                : "Could not send your request. Please try again.";
            this.setState({ submitting: false, errors: [message] });
            return false;
        }
    }
}
//# sourceMappingURL=LeadViewModel.js.map